/**
 * Global Supabase traffic tracker.
 *
 * Installed at app startup so the admin Supabase Traffic Monitor can show
 * historical request data from the moment the app loaded — not just from
 * when the monitor tab was opened.
 */

export interface TrafficLog {
  timestamp: number;
  type: 'REST' | 'Auth' | 'Edge Function' | 'Realtime' | 'Storage' | 'Other';
}

const SUPABASE_HOST = 'quidbuzjosjewriszapj.supabase.co';
const MAX_LOGS = 2000;

const logs: TrafficLog[] = [];
const subscribers = new Set<() => void>();
let installed = false;

function classify(url: string): TrafficLog['type'] {
  if (url.includes('/rest/v1')) return 'REST';
  if (url.includes('/auth/')) return 'Auth';
  if (url.includes('/functions/')) return 'Edge Function';
  if (url.includes('/realtime/')) return 'Realtime';
  if (url.includes('/storage/')) return 'Storage';
  return 'Other';
}

export function installSupabaseTrafficTracker() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = function patchedFetch(...args: Parameters<typeof fetch>) {
    try {
      const input = args[0];
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
          ? input.href
          : (input as Request)?.url || '';

      if (url.includes(SUPABASE_HOST)) {
        logs.push({ timestamp: Date.now(), type: classify(url) });
        if (logs.length > MAX_LOGS) logs.splice(0, logs.length - MAX_LOGS);
        subscribers.forEach((fn) => {
          try { fn(); } catch { /* noop */ }
        });
      }
    } catch {
      /* never break fetch */
    }
    return originalFetch(...args);
  };
}

export function getTrafficLogs(): TrafficLog[] {
  return logs.slice();
}

export function subscribeTraffic(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

export function resetTrafficLogs() {
  logs.length = 0;
  subscribers.forEach((fn) => {
    try { fn(); } catch { /* noop */ }
  });
}